export type RealtimeConnectionStatus = "connecting" | "connected" | "disconnected";

type RealtimeChannel = "visits" | "aggregate";

const realtimeUrl = (propertyId: string, channel: RealtimeChannel) => {
  const origin = new URL(window.location.origin);
  origin.protocol = origin.protocol === "https:" ? "wss:" : "ws:";
  origin.pathname = "/api/realtime";
  origin.search = `property=${encodeURIComponent(propertyId)}&channel=${encodeURIComponent(channel)}`;
  return origin.toString();
};

export const subscribeToVisitsRealtime = (
  propertyId: string,
  onUpdate: () => void,
  onStatusChange?: (status: RealtimeConnectionStatus) => void
) => {
  return subscribeToRealtime("visits", propertyId, onUpdate, onStatusChange);
};

export const subscribeToAggregateRealtime = (
  propertyId: string,
  onUpdate: () => void,
  onStatusChange?: (status: RealtimeConnectionStatus) => void
) => {
  return subscribeToRealtime("aggregate", propertyId, onUpdate, onStatusChange);
};

const subscribeToRealtime = (
  channel: RealtimeChannel,
  propertyId: string,
  onUpdate: () => void,
  onStatusChange?: (status: RealtimeConnectionStatus) => void
) => {
  let socket: WebSocket | null = null;
  let reconnectTimer: number | null = null;
  let closedManually = false;

  const clearReconnectTimer = () => {
    if (reconnectTimer !== null) {
      window.clearTimeout(reconnectTimer);
      reconnectTimer = null;
    }
  };

  const connect = () => {
    clearReconnectTimer();
    onStatusChange?.("connecting");
    socket = new WebSocket(realtimeUrl(propertyId, channel));

    socket.addEventListener("open", () => {
      onStatusChange?.("connected");
      onUpdate();
    });

    socket.addEventListener("message", (event) => {
      if (typeof event.data === "string") {
        try {
          JSON.parse(event.data);
        } catch {
          return;
        }
      }
      onUpdate();
    });

    socket.addEventListener("close", () => {
      socket = null;
      onStatusChange?.("disconnected");
      if (closedManually) return;
      reconnectTimer = window.setTimeout(connect, 2000);
    });

    socket.addEventListener("error", () => {
      socket?.close();
    });
  };

  connect();

  return () => {
    closedManually = true;
    clearReconnectTimer();
    onStatusChange?.("disconnected");
    socket?.close();
  };
};
